import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  Banknote,
  ClipboardList,
  Clock,
  ExternalLink,
  FileText,
  LockOpen,
  Moon,
  PackagePlus,
  Plus,
  ShoppingBag,
  Sun,
  UserPlus,
  UserRound,
} from 'lucide-react';
import {
  ORDER_STATUS_META,
  formatMoney,
  formatOrderNumber,
  formatPhone,
  hasAnyPermission,
  normalizeSearch,
  type CustomerDTO,
  type OrderListItemDTO,
  type Paginated,
  type ProductListItemDTO,
} from '@dezza/shared';
import {
  CommandPalette,
  Thumb,
  useDebouncedValue,
  useHotkey,
  useTheme,
  type CommandPaletteGroup,
  type CommandPaletteItem,
} from '@dezza/ui';
import { api } from '../lib/api';
import { useUpgrade } from '../lib/access';
import { useMe } from '../lib/auth';
import { EXTRA_PAGES, NAV_GROUPS, useNav } from './nav';
import { useShell } from './shell-store';

// ---------------------------------------------------------------- Recentes (salvos no aparelho)
type RecentKind = 'page' | 'product' | 'customer' | 'order';
interface RecentEntry {
  id: string;
  kind: RecentKind;
  label: string;
  hint?: string;
  to: string;
}

/** Recentes por loja: trocar de loja (ou dividir o aparelho) não mostra clientes e produtos de outra. */
const useRecent = create<{
  byCompany: Record<string, RecentEntry[]>;
  push: (companyId: string, e: RecentEntry) => void;
}>()(
  persist(
    (set) => ({
      byCompany: {},
      push: (companyId, e) =>
        set((s) => ({
          byCompany: {
            ...s.byCompany,
            [companyId]: [e, ...(s.byCompany[companyId] ?? []).filter((x) => x.id !== e.id)].slice(0, 6),
          },
        })),
    }),
    { name: 'dz.palette.recentes' },
  ),
);
const NO_RECENT: RecentEntry[] = [];

/**
 * Busca da paleta: todas as palavras digitadas precisam aparecer (sem acento, sem diferenciar
 * maiúsculas) no rótulo ou nos sinônimos. Começo do rótulo vale mais que começo de palavra,
 * que vale mais que trecho no meio e que só sinônimo. Evita "leg" achar "Nova despesa".
 */
export function paletteFilter(value: string, search: string, keywords?: string[]): number {
  const terms = normalizeSearch(search).split(/\s+/).filter(Boolean);
  if (!terms.length) return 1;
  const label = normalizeSearch(value.replace(/__[^_]*$/, ''));
  const hay = `${label} ${normalizeSearch((keywords ?? []).join(' '))}`;
  if (!terms.every((t) => hay.includes(t))) return 0;
  const phrase = terms.join(' ');
  if (label.startsWith(phrase)) return 1;
  if (label.split(/[\s·#,-]+/).some((w) => w.startsWith(terms[0]!))) return 0.8;
  if (label.includes(phrase)) return 0.6;
  return 0.4;
}

const KIND_ICON: Record<RecentKind, ReactNode> = {
  page: <Clock />,
  product: <PackagePlus />,
  customer: <UserRound />,
  order: <ClipboardList />,
};

/** Ícone de uma tela do menu pelo caminho (para os recentes). */
function pageIcon(to: string): ReactNode {
  for (const g of NAV_GROUPS) {
    const item = g.items.find((i) => i.to === to);
    if (item) {
      const Icon = item.icon;
      return <Icon />;
    }
  }
  return <Clock />;
}

/**
 * Paleta de comandos ligada ao app (Ctrl/Cmd + K, ou "/" fora de campos de texto):
 * telas do menu (com permissões e cadeado do plano), ações rápidas, busca ao vivo de produtos,
 * clientes e pedidos (com espera de 250 ms) e os últimos itens abertos, salvos no aparelho.
 */
export function CommandMenu() {
  const me = useMe();
  const navigate = useNavigate();
  const upgrade = useUpgrade();
  const nav = useNav();
  const open = useShell((s) => s.paletteOpen);
  const setOpen = useShell((s) => s.setPaletteOpen);
  const { resolvedTheme, toggleTheme } = useTheme();
  const companyId = me.company.id;
  const recent = useRecent((s) => s.byCompany[companyId] ?? NO_RECENT);
  const pushRecentFor = useRecent((s) => s.push);
  const pushRecent = (e: RecentEntry) => pushRecentFor(companyId, e);
  const [query, setQuery] = useState('');
  const q = useDebouncedValue(query.trim(), 250);
  const perms = me.membership.permissions;
  const can = (list: Parameters<typeof hasAnyPermission>[1]) => hasAnyPermission(perms, list);
  const live = open && q.length >= 2;
  const canOrders = can(['orders.manage', 'sales.create']);

  useHotkey('/', (e) => {
    e.preventDefault();
    setOpen(true);
  });

  const products = useQuery({
    queryKey: ['palette', 'products', q],
    queryFn: ({ signal }) =>
      api.get<Paginated<ProductListItemDTO>>('/products', { q, pageSize: 5 }, { signal, silent: true }),
    enabled: live,
    staleTime: 30_000,
  });
  const customers = useQuery({
    queryKey: ['palette', 'customers', q],
    queryFn: ({ signal }) =>
      api.get<Paginated<CustomerDTO>>('/customers', { q, pageSize: 5 }, { signal, silent: true }),
    enabled: live,
    staleTime: 30_000,
  });
  const orders = useQuery({
    queryKey: ['palette', 'orders', q],
    queryFn: ({ signal }) =>
      api.get<Paginated<OrderListItemDTO>>('/orders', { q, pageSize: 5 }, { signal, silent: true }),
    enabled: live && canOrders,
    staleTime: 15_000,
  });
  const searching = live && (products.isFetching || customers.isFetching || orders.isFetching);

  const go = (to: string, entry?: Omit<RecentEntry, 'to'>) => {
    if (entry) pushRecent({ ...entry, to });
    navigate(to);
  };

  const groups = useMemo<CommandPaletteGroup[]>(() => {
    const out: CommandPaletteGroup[] = [];

    if (!query.trim() && recent.length) {
      out.push({
        heading: 'Recentes',
        items: recent.map((r) => ({
          id: `recent:${r.id}`,
          label: r.label,
          hint: r.hint,
          icon: r.kind === 'page' ? pageIcon(r.to) : KIND_ICON[r.kind],
          onSelect: () => go(r.to, r),
        })),
      });
    }

    const actions: CommandPaletteItem[] = [];
    if (can(['sales.create']))
      actions.push({
        id: 'a:venda',
        label: 'Nova venda',
        icon: <ShoppingBag />,
        keywords: ['vender', 'pdv', 'caixa'],
        onSelect: () => navigate('/vender'),
      });
    if (can(['products.manage']))
      actions.push({
        id: 'a:produto',
        label: 'Novo produto',
        icon: <Plus />,
        keywords: ['cadastrar produto'],
        onSelect: () => navigate('/produtos/novo'),
      });
    if (can(['customers.manage']))
      actions.push({
        id: 'a:cliente',
        label: 'Novo cliente',
        icon: <UserPlus />,
        keywords: ['cadastrar cliente'],
        onSelect: () => navigate('/clientes/novo'),
      });
    if (can(['finance.manage']))
      actions.push({
        id: 'a:despesa',
        label: 'Nova despesa',
        icon: <Banknote />,
        keywords: ['conta a pagar', 'lançamento', 'gasto'],
        onSelect: () => navigate('/financas/lancamentos?tipo=despesa&novo=despesa'),
      });
    if (can(['cash.operate']))
      actions.push({
        id: 'a:caixa',
        label: 'Abrir caixa',
        icon: <LockOpen />,
        keywords: ['fechar caixa', 'sangria', 'suprimento'],
        onSelect: () => navigate('/financas/caixa'),
      });
    if (me.catalog?.isPublished && me.catalog.url) {
      const url = me.catalog.url;
      actions.push({
        id: 'a:catalogo',
        label: 'Ver catálogo',
        hint: 'Abre em outra aba',
        icon: <ExternalLink />,
        keywords: ['loja online', 'vitrine', 'site'],
        onSelect: () => window.open(url, '_blank', 'noopener'),
      });
    } else if (can(['catalog.manage'])) {
      actions.push({
        id: 'a:catalogo',
        label: 'Ver catálogo',
        icon: <ExternalLink />,
        keywords: ['loja online', 'vitrine'],
        onSelect: () => navigate('/catalogo'),
      });
    }
    actions.push({
      id: 'a:tema',
      label: resolvedTheme === 'dark' ? 'Usar tema claro' : 'Usar tema escuro',
      icon: resolvedTheme === 'dark' ? <Sun /> : <Moon />,
      keywords: ['tema', 'aparência', 'modo escuro', 'modo claro'],
      onSelect: toggleTheme,
    });
    out.push({ heading: 'Ações', items: actions });

    const pages: CommandPaletteItem[] = [];
    for (const g of nav) {
      for (const item of g.items) {
        const Icon = item.icon;
        pages.push({
          id: `p:${item.key}`,
          label: item.label,
          hint: item.locked ? 'Outro plano' : g.label,
          icon: <Icon />,
          keywords: item.keywords,
          onSelect: () => {
            if (item.locked && item.feature) return upgrade({ feature: item.feature });
            go(item.to, { id: `page:${item.key}`, kind: 'page', label: item.label, hint: g.label });
          },
        });
      }
    }
    for (const p of EXTRA_PAGES) {
      if (p.perm && !can(p.perm)) continue;
      pages.push({
        id: `x:${p.key}`,
        label: p.label,
        hint: p.hint,
        icon: <FileText />,
        keywords: p.keywords,
        onSelect: () => go(p.to, { id: `extra:${p.key}`, kind: 'page', label: p.label, hint: p.hint }),
      });
    }
    out.push({ heading: 'Telas', items: pages });

    if (live) {
      // Os resultados já vêm filtrados da API (nome, código, telefone, número): a busca do campo
      // entra como palavra-chave para a paleta não esconder o que a API achou.
      const kw = [q];
      const productItems = (products.data?.items ?? []).map<CommandPaletteItem>((p) => ({
        id: `prod:${p.id}`,
        label: p.name,
        hint: formatMoney(p.promoPriceCents ?? p.priceCents),
        icon: <Thumb src={p.thumbUrl} name={p.name} size={22} rounded="lg" />,
        keywords: [...kw, p.code ?? '', p.barcode ?? ''],
        onSelect: () =>
          go(`/produtos/${p.id}`, { id: `product:${p.id}`, kind: 'product', label: p.name, hint: 'Produto' }),
      }));
      if (productItems.length) out.push({ heading: 'Produtos', items: productItems });

      const customerItems = (customers.data?.items ?? []).map<CommandPaletteItem>((c) => ({
        id: `cli:${c.id}`,
        label: c.name,
        hint: c.phone ? formatPhone(c.phone) : undefined,
        icon: <UserRound />,
        keywords: [...kw, c.phone ?? '', c.email ?? ''],
        onSelect: () =>
          go(`/clientes/${c.id}`, {
            id: `customer:${c.id}`,
            kind: 'customer',
            label: c.name,
            hint: 'Cliente',
          }),
      }));
      if (customerItems.length) out.push({ heading: 'Clientes', items: customerItems });

      const orderItems = (orders.data?.items ?? []).map<CommandPaletteItem>((o) => {
        const label = `${formatOrderNumber(o.number)}${o.customerName ? ` · ${o.customerName}` : ''}`;
        return {
          id: `ped:${o.id}`,
          label,
          hint: `${formatMoney(o.totalCents)} · ${ORDER_STATUS_META[o.status].short}`,
          icon: <ClipboardList />,
          keywords: [...kw, String(o.number)],
          onSelect: () =>
            go(`/pedidos/${o.id}`, { id: `order:${o.id}`, kind: 'order', label, hint: 'Pedido' }),
        };
      });
      if (orderItems.length) out.push({ heading: 'Pedidos', items: orderItems });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    query,
    q,
    live,
    recent,
    nav,
    perms,
    resolvedTheme,
    me.catalog,
    products.data,
    customers.data,
    orders.data,
  ]);

  return (
    <CommandPalette
      groups={groups}
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setQuery('');
      }}
      onSearchChange={setQuery}
      filter={paletteFilter}
      loading={searching}
      placeholder="Buscar telas, ações, produtos, clientes e pedidos"
      emptyText={
        searching
          ? 'Buscando...'
          : query.trim().length === 1
            ? 'Digite mais uma letra para buscar produtos e clientes.'
            : 'Nada encontrado. Tente o nome, o código ou o telefone.'
      }
      footer="Tecle / em qualquer tela para buscar"
    />
  );
}
